import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const ReportsPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchReports();
    }
  }, [isAdmin]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      // D'abord récupérer les reports avec status="pending"
      const { data: reportsData, error: reportsError } = await supabase
        .from('reports')
        .select(`
          *,
          event:event_id (
            id,
            title,
            status,
            organizer_id,
            start_date,
            city
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (reportsError) throw reportsError;

      // Filtrer uniquement les événements publiés
      const publishedReports = reportsData?.filter(r => r.event?.status === 'published') || [];

      // Ensuite récupérer les infos des utilisateurs
      if (publishedReports.length > 0) {
        const userIds = [...new Set(publishedReports.map(r => r.user_id))];
        
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('id, organizer_email, organizer_firstname, organizer_lastname')
          .in('id', userIds);

        if (usersError) {
          console.error('Erreur lors de la récupération des profils:', usersError);
        }

        // Enrichir les reports avec les infos utilisateur
        const enrichedReports = publishedReports.map(report => ({
          ...report,
          user: usersData?.find(u => u.id === report.user_id) || null
        }));

        // Regrouper par événement
        const groupedByEvent = {};
        enrichedReports.forEach(report => {
          const eventId = report.event_id;
          if (!groupedByEvent[eventId]) {
            groupedByEvent[eventId] = {
              event: report.event,
              reports: [],
              count: 0
            };
          }
          groupedByEvent[eventId].reports.push(report);
          groupedByEvent[eventId].count++;
        });

        // Convertir en tableau et trier par nombre de signalements (décroissant)
        const groupedArray = Object.values(groupedByEvent).sort((a, b) => b.count - a.count);

        setReports(groupedArray);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des signalements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetToDraft = async (eventGroup) => {
    if (!eventGroup.event) {
      alert(t('errors.eventNotFound'));
      return;
    }

    setProcessingId(eventGroup.event.id);
    try {
      // Créer un message récapitulatif de tous les signalements
      const reportMessages = eventGroup.reports
        .map((r, i) => `${i + 1}. ${r.message}`)
        .join('\n');

      // Mettre l'événement en brouillon avec tous les messages de signalement et updated_at
      const { error: updateError } = await supabase
        .from('events')
        .update({
          status: 'draft',
          message: `${t('settings.admin.reports.reportMessage')} (${eventGroup.count}):\n${reportMessages}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', eventGroup.event.id);

      if (updateError) throw updateError;

      // Supprimer tous les signalements de cet événement
      const { error: deleteError } = await supabase
        .from('reports')
        .delete()
        .in('id', eventGroup.reports.map(r => r.id));

      if (deleteError) throw deleteError;

      // Rediriger vers la page d'édition
      navigate(`/edit-event/${eventGroup.event.id}`);
    } catch (error) {
      console.error('Erreur lors du traitement du signalement:', error);
      alert(t('settings.admin.reports.draftError'));
      setProcessingId(null);
    }
  };

  const handleViewEvent = (eventId) => {
    navigate(`/event/${eventId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (adminLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <PageHeader title={t('settings.admin.reports.title')} backPath="/settings" />

      <div className="p-4">
        {loading ? (
          <LoadingSpinner message={t('common.loading') + '...'} />
        ) : reports.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <span className="text-6xl mb-4 block">✅</span>
            <p className="text-gray-500 text-lg">{t('settings.admin.reports.noReports')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((eventGroup) => (
              <div key={eventGroup.event?.id || 'unknown'} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Événement concerné */}
                <div className="bg-gray-50 p-4 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-h3 text-gray-900">
                          {eventGroup.event ? eventGroup.event.title : t('errors.eventNotFound')}
                        </h3>
                        <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs font-bold">
                          {eventGroup.count} 
                          {/* {eventGroup.count > 1 ? t('settings.admin.reports.reports') : t('settings.admin.reports.report')} */}
                        </span>
                      </div>
                      {eventGroup.event && (
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>📍 {eventGroup.event.city}</p>
                          <p>📅 {new Date(eventGroup.event.start_date).toLocaleDateString()}</p>
                          <p className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              eventGroup.event.status === 'published' ? 'bg-green-100 text-green-800' :
                              eventGroup.event.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                              eventGroup.event.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {t(`eventStatus.${eventGroup.event.status}`)}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Liste des signalements */}
                <div className="p-4">
                  <div className="space-y-3 mb-4">
                    {eventGroup.reports.map((report, index) => (
                      <div key={report.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <span className="text-xl">⚠️</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {t('settings.admin.reports.reportedBy')}: 
                            {report.user ? (
                              <>
                                {' '}{report.user.organizer_firstname} {report.user.organizer_lastname}
                                {report.user.organizer_email && (
                                  <span className="text-gray-600"> ({report.user.organizer_email})</span>
                                )}
                              </>
                            ) : (
                              <>{' '}{t('settings.admin.reports.anonymousUser')}</>
                            )}
                          </p>
                          <p className="text-sm text-gray-700">
                            {report.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(report.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  {eventGroup.event && (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleViewEvent(eventGroup.event.id)}
                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      >
                        👁️ {t('settings.admin.reports.viewEvent')}
                      </button>
                      <button
                        onClick={() => handleSetToDraft(eventGroup)}
                        disabled={processingId === eventGroup.event.id || eventGroup.event.status === 'draft'}
                        className="flex-1 px-4 py-2 bg-bleu1 text-white rounded-lg hover:bg-bleu3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        {processingId === eventGroup.event.id ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            {t('common.loading')}
                          </span>
                        ) : (
                          <>✏️ {t('settings.admin.reports.setToDraft')}</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
